// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The three modals' copy, kept out of main.ts so that the prose can be read and
 * corrected as prose.
 *
 * EVERY NUMBER IN HERE WAS VERIFIED FIRST-HAND during the build, and where a
 * claim about another product could not be verified it is not made. The MTEB
 * table below was re-fetched cell by cell from the official results repository
 * by two independent agents; all twenty cells matched to the last digit.
 */

import { MODEL_LICENSE, MODEL_REPO, MODEL_REV } from './model.ts';
import { term } from './glossary.ts';

export const HOW_IT_WORKS = `
<h3>1 — Your file is read in this tab</h3>
<p>
  A CSV, TSV or JSONL is streamed through <code>File.stream()</code> and parsed here. topicgap
  profiles every column and proposes the one that holds free text; you can override it, and the
  three sample values shown under the picker are how you check the guess.
</p>

<h3>2 — Each row becomes ${term('an embedding', 'embedding')}</h3>
<p>
  The model is <code>${MODEL_REPO}</code> (${MODEL_LICENSE}), a 15 MB lookup table pinned to
  revision <code>${MODEL_REV.slice(0, 12)}…</code>. It is a <em>static</em> embedding model: there
  is no neural network to run, no WebGPU, no WASM runtime. A row is tokenised with
  ${term('WordPiece', 'wordpiece')}, the matching table rows are averaged, and the result is scaled
  to unit length. That is the whole of inference, and it is why a corpus of 25,000 rows takes
  seconds rather than minutes.
</p>

<h3>3 — Rows are grouped with ${term('spherical k-means')}</h3>
<p>
  Grouping happens in the full 128 dimensions, not in a two-dimensional picture of them. Everything
  is seeded, so the same file at the same <em>k</em> and the same seed gives byte-identical clusters
  — you can regenerate an export and diff it.
</p>

<h3>4 — Clusters are named with ${term('c-TF-IDF')}, not by a language model</h3>
<p>
  Each cluster's label is the terms that are common inside it and rare everywhere else. It is
  arithmetic over word counts. Nothing is sent anywhere to be named, the same input always produces
  the same label, and you can rename any cluster — the new name flows into every export.
</p>

<h3>5 — Your eval set is measured against those clusters</h3>
<p>
  If you load a second file, each of its rows is embedded into the same space and matched to its
  nearest cluster centre. It only counts as <em>covering</em> that cluster if it also lands inside
  the cluster's ${term('radius')}. Then you leave with three files: your rows with a topic attached,
  a topics report with the uncovered ones first, and a curation sample.
</p>

<h3>Why this model</h3>
<p>
  Clustering quality is not the same thing as retrieval quality, and the difference is large. These
  are v-measure scores on MTEB's clustering tasks, read from the official results repository at each
  model's own pinned revision:
</p>
<div class="table-scroll">
<table>
  <thead>
    <tr><th>task</th><th>potion-base-4M</th><th>all-MiniLM-L6-v2</th><th>static-retrieval-mrl</th></tr>
  </thead>
  <tbody>
    <tr><td>StackExchangeClustering.v2</td><td class="best">50.91</td><td>45.17</td><td>30.39</td></tr>
    <tr><td>ArXivHierarchicalClusteringS2S</td><td>49.76</td><td class="best">54.54</td><td>38.60</td></tr>
    <tr><td>TwentyNewsgroupsClustering.v2</td><td>32.69</td><td class="best">46.02</td><td>19.78</td></tr>
    <tr><td>MedrxivClusteringS2S.v2</td><td>28.19</td><td class="best">35.44</td><td>24.06</td></tr>
    <tr><td>BiorxivClusteringP2P.v2</td><td>31.57</td><td class="best">40.99</td><td>33.47</td></tr>
  </tbody>
</table>
</div>
<p>
  <strong>StackExchangeClustering</strong> is the closest public analogue to what you are about to
  load — short, user-written, one question per row — and on it this 15 MB lookup table beats
  <code>all-MiniLM-L6-v2</code>, the six-layer transformer that browser clustering tools normally
  run. It does <em>not</em> win everywhere: on long scientific abstracts the transformer is clearly
  better, and the table says so rather than quoting only the row that flatters it. The model the
  original specification for this tool named, <code>static-retrieval-mrl-en-v1</code>, is last in
  four columns of five — it is an excellent retrieval model being asked to do a different job.
</p>
`;

export const PRIVACY = `
<p>
  The corpus this tool is for — support tickets, logged production prompts — is the dataset with the
  strictest handling rules in most organisations. It is also the one that most needs the analysis.
  That is the entire reason topicgap runs where it does.
</p>

<h3>Protected</h3>
<ul class="good-list">
  <li><strong>Your rows never leave the device.</strong> They are parsed, tokenised, embedded,
    clustered and labelled inside this tab. There is no upload endpoint in the code — not a disabled
    one, not an opt-in one. There is no code path that could send them.</li>
  <li><strong>Your eval set never leaves the device either</strong>, and it is often the more
    sensitive of the two files, because it encodes what you are testing for.</li>
  <li><strong>Nothing is written to disk.</strong> No IndexedDB, no Origin Private File System, no
    cookies. Only the two model files go into the Cache API, and only your interface settings go into
    <code>localStorage</code>. A build gate fails the release if storage APIs appear in the bundle.</li>
  <li><strong>Cluster labels drop anything appearing in only one row</strong>, which in a support
    corpus is where order numbers, ticket references, email addresses and customer names live. The
    exported <code>topics.md</code> is far less likely to carry personal data than the rows it
    summarises.</li>
  <li><strong>Exports are assembled here</strong> and handed straight to the browser's download.</li>
</ul>

<h3>Not protected</h3>
<ul class="bad-list">
  <li><strong>Hugging Face sees that you fetched the model.</strong> Two anonymous GETs of constant
    URLs with <code>credentials: 'omit'</code>, on first run only. They reveal your IP address and
    which file you asked for, and nothing whatever about your data. Afterwards the tool runs with the
    network switched off — which you can check in a network inspector.</li>
  <li><strong>A Cloudflare Web Analytics beacon records anonymous page views</strong> — no cookies,
    no fingerprinting, no cross-site tracking; your files and data are never sent to it.</li>
  <li><strong>Feedback you choose to send</strong> goes to <code>feedback.benrichardson.dev</code>.
    Nothing is sent unless you open the feedback form and press Send; your files and data never
    are.</li>
  <li><strong>Your own exports.</strong> <code>labelled.csv</code> contains your rows by design, and
    <code>sample.jsonl</code> contains ten rows per cluster verbatim. Where they go next is on you.</li>
</ul>

<h3>Trust surface</h3>
<ul>
  <li>The static bundle served by GitHub Pages, and the TLS chain to it.</li>
  <li><code>huggingface.co</code> and its CDN, for two public files pinned to an immutable revision.</li>
  <li>The Cloudflare Web Analytics beacon, and the feedback endpoint when you use it.</li>
</ul>

<h3>What this tool refuses to tell you</h3>
<p>
  It will not tell you that your eval set is <em>good</em>, and it will not call these your "real
  topics". They are the clusters at the <em>k</em> you chose, and a different <em>k</em> gives a
  different set. Every coverage number is printed beside its ${term('chance level')} — what an eval
  set of the same size drawn at random from your own corpus would have covered — because a number
  without its chance level looks exactly like a finding. And it prints a
  ${term('detection floor')}: below a certain cluster size, your eval set was never large enough to
  demonstrate a gap at all, and topicgap says so instead of reporting one.
</p>
`;

export const ABOUT = `
<p>
  <strong>topicgap</strong> clusters your own text on-device, names every cluster without a language
  model, and answers one question a map cannot: which of those topics your eval set has never
  covered. You leave with your rows labelled, a topics report and a curation sample.
</p>

<h3>Honestly, about the alternatives</h3>
<p>
  Two free, no-signup, fully client-side tools already cluster and label a CSV in a browser, and
  pretending otherwise would be falsifiable in one search.
  <strong>Apple's Embedding Atlas</strong> (MIT) draws the map, and draws it beautifully. Its export
  is <code>COPY (SELECT * FROM …)</code> over a lasso selection — you get rows back, but no topic
  column, and its cluster labels are recomputed for whatever is on screen rather than attached to
  rows. <strong>Graph My Keywords</strong> is client-side too and does emit a per-row
  <code>cluster_label</code>, and even compares a second file — but it is a keyword tool that scores
  no cell longer than 80 characters, it replaces your columns with its own SEO schema, and it stops
  at 5,000 rows.
</p>
<p>
  So topicgap's difference is narrower than "nobody does this", and it is this: your own rows come
  back intact with a topic attached, over arbitrary free text, at 25,000 rows — plus a curation
  sample of the most central <em>and</em> most peripheral rows per topic, which nothing else
  produces, and a coverage report framed as production-versus-eval rather than you-versus-a-rival.
</p>

<h3>And its sibling</h3>
<p>
  <a href="https://evalspill.benrichardson.dev" target="_blank" rel="noopener">evalspill</a> asks
  which eval rows <em>leaked out of training</em> and hands back a smaller, cleaned eval set.
  topicgap asks which topics the eval set <em>never reached</em> and hands back a seed for a bigger
  one. Contamination subtracts; coverage adds.
</p>

<h3>Built by</h3>
<p>
  <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> — see the
  whole catalogue at
  <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>.
  Source: <a href="https://github.com/ben-gy/topicgap" target="_blank" rel="noopener">github.com/ben-gy/topicgap</a>.
</p>
<p>
  Keyboard: <code>Esc</code> closes anything, <code>Enter</code> runs the analysis,
  <code>Cmd/Ctrl+V</code> pastes a file from the clipboard.
</p>
`;
